/**
 * GRI Landing Page — Cannon Bundle
 * File: assets/gri-landing-page.js
 *
 * Used by: sections/gri-landing-page.liquid
 *
 * SCOPING: Every query in this file is scoped to elements inside
 * `.landed-lp[data-section-id]`. This file does NOT touch the Mega
 * Blaster section (which has its own gri-mb-landing.js). They can
 * never collide even if both sections render on the same page.
 *
 * iOS SAFARI HARDENING: variant IDs are trimmed and validated before
 * any fetch() call. Safari throws "The string did not match the
 * expected pattern" when fetch bodies contain NaN or invalid numbers.
 *
 * CART INTEGRATION: After a successful /cart/add.js, we manually
 * fetch ?section_id=cart-drawer and replaceWith() the items, footer,
 * empty-state markup, and is-empty classes — covering all five
 * elements that change between empty and populated cart states.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     SCOPING HELPERS
  ───────────────────────────────────────────── */
  // Find every cannon-bundle landing-page wrapper on the page so we
  // can attach listeners scoped to each one. (Unlikely to have more
  // than one, but defensive.)
  function getLandingSections() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.landed-lp[data-section-id]')
    ).filter(function (el) {
      // Only the cannon-bundle landing page. Mega Blaster has its own
      // wrapper class via the .gri-mb-wrapper parent — skip those.
      return !el.closest('.gri-mb-wrapper');
    });
  }

  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function showToast(msg) {
    var t = document.querySelector('.gri-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'gri-toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.classList.remove('is-visible'); }, 2800);
  }

  /* ─────────────────────────────────────────────
     1. VARIANT TILE SELECTION (per section)
  ───────────────────────────────────────────── */
  function initVariantTiles(section) {
    var grid = qs('.lp-bb__tiles[id]', section);
    if (!grid) return;

    var tiles = qsa('.lp-bb__tile[data-i]', grid);
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        tiles.forEach(function (t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tile.classList.add('is-selected');
        tile.setAttribute('aria-checked', 'true');

        var variantId = sanitiseVariantId(tile.getAttribute('data-variant-id'));
        if (variantId) {
          // Update only THIS section's CTA buttons (and its sticky bar).
          qsa('.gri-atc-btn', section).forEach(function (btn) {
            btn.setAttribute('data-variant-id', variantId);
          });
          // The sticky bar lives outside .landed-lp, find it by section id.
          var sectionId = section.getAttribute('data-section-id');
          if (sectionId) {
            var stickyCta = document.querySelector(
              '#gri-sticky-' + sectionId + ' .lp-sticky__cta'
            );
            if (stickyCta) stickyCta.setAttribute('data-variant-id', variantId);
          }
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     VARIANT ID SANITISATION (iOS Safari fix)
  ───────────────────────────────────────────── */
  // iOS Safari is strict about fetch bodies. Returns a clean numeric
  // string, or null if the input isn't a valid positive integer.
  function sanitiseVariantId(raw) {
    if (raw == null) return null;
    var s = String(raw).trim();
    if (!s) return null;
    // Must be all digits — no whitespace, no letters, no symbols.
    if (!/^\d+$/.test(s)) return null;
    var n = Number(s);
    if (!isFinite(n) || n <= 0) return null;
    return s;
  }

  /* ─────────────────────────────────────────────
     2. ADD TO CART (per section)
  ───────────────────────────────────────────── */
  function initAddToCart(section) {
    // CTA buttons inside this section, plus the sticky bar that
    // lives outside .landed-lp but is associated with this section.
    var sectionId = section.getAttribute('data-section-id');
    var inSection = qsa('.gri-atc-btn', section);
    var stickyBtns = sectionId
      ? qsa('#gri-sticky-' + sectionId + ' .gri-atc-btn')
      : [];
    var btns = inSection.concat(stickyBtns);

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Resolve the variant ID at click time. Priority:
        // 1. The currently selected tile in this section's tile grid
        // 2. The button's own data-variant-id
        var selectedTile = qs('.lp-bb__tile.is-selected[data-variant-id]', section);
        var raw = selectedTile && selectedTile.getAttribute('data-variant-id');
        if (!raw) raw = btn.getAttribute('data-variant-id');

        var variantId = sanitiseVariantId(raw);

        if (variantId) {
          addToCart(btn, variantId);
          return;
        }

        // No usable variant id — try the product handle fallback.
        var handle = btn.getAttribute('data-product-handle');
        if (handle) {
          fetch('/products/' + encodeURIComponent(handle) + '.js', {
            credentials: 'same-origin'
          })
            .then(function (r) { return r.text(); })
            .then(function (text) {
              var p = null;
              try { p = JSON.parse(text); } catch (e) {}
              if (!p) {
                // Got HTML or non-JSON — fall back to navigating
                // the user to the product page rather than throwing.
                window.location.href = '/products/' + handle;
                return;
              }
              var first = p.variants && p.variants.find(function (v) { return v.available; });
              if (!first) first = p.variants && p.variants[0];
              if (first) {
                var fallbackId = sanitiseVariantId(first.id);
                if (fallbackId) addToCart(btn, fallbackId);
                else window.location.href = '/products/' + handle;
              } else {
                window.location.href = '/products/' + handle;
              }
            })
            .catch(function () { window.location.href = '/products/' + handle; });
          return;
        }

        showToast('Please choose a colour first.');
      });
    });
  }

  function addToCart(btn, variantId) {
    var label = btn.querySelector('.lp-fhero__buy-label') ||
                btn.querySelector('.lp-sticky__cta-label') || btn;
    var originalText = label.textContent;

    btn.setAttribute('data-loading', '1');
    btn.disabled = true;
    label.textContent = 'Adding…';

    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    // Single attempt — uses FormData, the same payload Dawn's product
    // form uses. Returns a normalised result.
    function attempt() {
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      return fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
          // NB: no Content-Type — browser auto-sets for FormData.
        },
        body: formData
      }).then(function (response) {
        return response.text().then(function (text) {
          var data = null;
          try { data = JSON.parse(text); } catch (e) {}
          return { ok: response.ok, status: response.status, data: data };
        });
      });
    }

    // Retry helper — silent retries on transient failures so the
    // user never sees a temporary 429/conflict. Real customers click
    // once and the request just works; this is for the edge cases
    // where Shopify briefly throttles an in-flight request.
    //
    // Retry on:
    //   429 (rate limited briefly)
    //   409 (conflict with another cart write)
    //   non-JSON body with 2xx/3xx (proxy/redirect glitch)
    //   network errors (transient connectivity)
    //
    // We DON'T retry on:
    //   422 (real validation error — out of stock, bad variant id)
    //   404 (variant doesn't exist)
    //   any other 4xx/5xx with a JSON error body
    function shouldRetry(res, err) {
      if (err) return true;                          // network error
      if (!res) return true;                          // no response
      if (res.status === 429) return true;            // rate limited
      if (res.status === 409) return true;            // conflict
      if (res.ok && !res.data) return true;           // 2xx but body wasn't JSON
      if (res.status >= 500) return true;             // server error
      return false;
    }

    // Try up to 2 attempts total: original + 1 quick retry on 429/409.
    // Total worst case ~1.2 seconds. If both fail, show a clear error
    // and re-enable the button immediately so the user can click
    // again rather than waiting forever.
    function tryWithRetries(attemptsLeft, delayMs) {
      return attempt()
        .then(function (res) {
          if (shouldRetry(res) && attemptsLeft > 0) {
            console.warn('[GRI] Retry (status ' + res.status +
                         ', ' + attemptsLeft + ' left, in ' + delayMs + 'ms)');
            return new Promise(function (resolve) {
              setTimeout(function () {
                tryWithRetries(attemptsLeft - 1, delayMs).then(resolve);
              }, delayMs);
            });
          }
          return res;
        })
        .catch(function (err) {
          if (attemptsLeft > 0) {
            console.warn('[GRI] Network error, retrying:', err);
            return new Promise(function (resolve) {
              setTimeout(function () {
                tryWithRetries(attemptsLeft - 1, delayMs).then(resolve);
              }, delayMs);
            });
          }
          throw err;
        });
    }

    tryWithRetries(1, 600)
      .then(function (res) {
        if (!res || !res.ok) {
          var errMsg = (res && res.data && (res.data.description || res.data.message)) ||
                       'This item could not be added.';
          var err = new Error(errMsg);
          if (res) err.status = res.status;
          throw err;
        }
        if (!res.data) {
          throw new Error('Cart response was not valid.');
        }
        return res.data;
      })
      .then(function (data) {
        label.textContent = 'Added ✓';
        showToast('Added to cart! 🎉');

        return refreshCartDrawer().then(function () {
          if (typeof window.publish === 'function' &&
              window.PUB_SUB_EVENTS &&
              window.PUB_SUB_EVENTS.cartUpdate) {
            try {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'gri-landing-atc',
                cartData: data,
                variantId: Number(variantId)
              });
            } catch (e) {}
          }
          openCartDrawer();
        });
      })
      .then(function () {
        setTimeout(function () {
          label.textContent = originalText;
          btn.removeAttribute('data-loading');
          btn.disabled = false;
        }, 1800);
      })
      .catch(function (err) {
        console.error('[GRI] Add to cart error:', err);
        label.textContent = originalText;
        btn.removeAttribute('data-loading');
        btn.disabled = false;

        // Filter out technical errors. The user should never see
        // raw JS error messages.
        var msg = String((err && err.message) || '');
        var friendly;
        if (/Unexpected token|not valid JSON|JSON\.parse|<!DOCTYPE|SyntaxError|<html/i.test(msg)) {
          friendly = 'Could not add to cart. Please try again.';
        } else if (msg && msg.length < 120 && !/[<>{}]/.test(msg)) {
          friendly = msg;
        } else {
          friendly = 'Could not add to cart. Please try again.';
        }
        showToast(friendly);
      });
  }

  /* ─────────────────────────────────────────────
     CART DRAWER REFRESH (Dawn-aware)
     Replaces items, footer, empty-state markup, and is-empty
     classes — all five things that change between empty and
     populated cart states in this theme.
  ───────────────────────────────────────────── */
  function refreshCartDrawer() {
    return Promise.all([
      fetch('/?section_id=cart-drawer', {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      }).then(function (r) { return r.text(); }),
      fetch('/?section_id=cart-icon-bubble', {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      }).then(function (r) { return r.text(); })
    ]).then(function (texts) {
      var drawerHtml = new DOMParser().parseFromString(texts[0], 'text/html');
      var iconHtml   = new DOMParser().parseFromString(texts[1], 'text/html');

      // 1. Items list
      var newItems = drawerHtml.querySelector('cart-drawer-items');
      var curItems = document.querySelector('cart-drawer-items');
      if (newItems && curItems) curItems.replaceWith(newItems);

      // 2. Footer (totals, checkout, shipping protection)
      var newFooter = drawerHtml.querySelector('.cart-drawer__footer');
      var curFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && curFooter) curFooter.replaceWith(newFooter);

      // 3. Empty-state block — remove if cart now has items, inject if empty
      var newEmpty = drawerHtml.querySelector('.drawer__inner-empty');
      var curEmpty = document.querySelector('.drawer__inner-empty');
      if (!newEmpty && curEmpty) {
        curEmpty.remove();
      } else if (newEmpty && !curEmpty) {
        var drawerInner = document.querySelector('cart-drawer .drawer__inner');
        if (drawerInner) drawerInner.insertBefore(newEmpty, drawerInner.firstChild);
      }

      // 4. is-empty class on the parent <cart-drawer>
      var newDrawer = drawerHtml.querySelector('cart-drawer');
      var curDrawer = document.querySelector('cart-drawer');
      if (newDrawer && curDrawer) {
        curDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }

      // 5. Drawer header item count "(3)"
      var newCount = drawerHtml.querySelector('#drawer__heading-count');
      var curCount = document.querySelector('#drawer__heading-count');
      if (newCount && curCount) curCount.textContent = newCount.textContent;

      // 6. Cart icon bubble in the header
      var newBubble = iconHtml.querySelector('.cart-count-bubble, [data-cart-count]');
      var curBubble = document.querySelector('.cart-count-bubble, [data-cart-count]');
      if (newBubble && curBubble) {
        curBubble.replaceWith(newBubble);
      } else if (newBubble && !curBubble) {
        var iconWrap = document.getElementById('cart-icon-bubble') ||
                       document.querySelector('a[href*="/cart"]');
        if (iconWrap) iconWrap.appendChild(newBubble);
      }
    }).catch(function (e) {
      console.warn('[GRI] Drawer refresh failed:', e);
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-empty');
    if (typeof drawer.open === 'function') {
      try { drawer.open(); return; } catch (e) {}
    }
    drawer.classList.add('animate', 'active');
  }

  /* ─────────────────────────────────────────────
     MARQUEE (per section)
  ───────────────────────────────────────────── */
  function initMarquee(marquee, speed) {
    var offset = 0;
    var halfWidth = 0;
    var dragging = false;
    var lastX = 0;

    function measure() { halfWidth = marquee.scrollWidth / 2; }

    function tick() {
      if (!dragging) {
        offset -= speed;
        if (offset <= -halfWidth) offset += halfWidth;
      }
      marquee.style.transform = 'translateX(' + offset + 'px)';
      requestAnimationFrame(tick);
    }

    function onDown(x) { dragging = true; lastX = x; }
    function onMove(x) {
      if (!dragging) return;
      var dx = x - lastX;
      offset += dx;
      if (offset > 0) offset -= halfWidth;
      if (offset < -halfWidth) offset += halfWidth;
      lastX = x;
    }
    function onUp() { dragging = false; }

    var wrap = marquee.parentElement;
    wrap.addEventListener('mousedown', function (e) { onDown(e.clientX); });
    window.addEventListener('mousemove', function (e) { if (dragging) onMove(e.clientX); });
    window.addEventListener('mouseup', onUp);
    wrap.addEventListener('touchstart', function (e) { onDown(e.touches[0].clientX); }, { passive: true });
    wrap.addEventListener('touchmove', function (e) { if (dragging) onMove(e.touches[0].clientX); }, { passive: true });
    wrap.addEventListener('touchend', onUp);

    measure();
    window.addEventListener('resize', measure);
    requestAnimationFrame(tick);
  }

  function initUGCRail(section) {
    qsa('.lp-ugc__marquee', section).forEach(function (marquee) {
      var wrap = marquee.parentElement;
      var origCards = Array.prototype.slice.call(marquee.children);
      if (!origCards.length) return;

      origCards.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        marquee.appendChild(clone);
      });

      function autoplayAll() {
        qsa('video', marquee).forEach(function (v) {
          v.muted = true; v.play().catch(function () {});
        });
      }
      autoplayAll();

      marquee.addEventListener('click', function (e) {
        var btn = e.target.closest('.lp-ugc__play');
        if (!btn) return;
        e.stopPropagation();
        var card = btn.closest('.lp-ugc__card');
        var v = qs('video', card);
        if (!v) return;
        if (v.muted) {
          qsa('video', marquee).forEach(function (o) {
            o.muted = true; o.closest('.lp-ugc__card').classList.remove('has-sound');
          });
          v.muted = false; card.classList.add('has-sound');
        } else { v.muted = true; card.classList.remove('has-sound'); }
      });

      var wrapId = wrap.id;
      if (wrapId) {
        qsa('[data-wrap="' + wrapId + '"]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (btn.classList.contains('gri-ugc-pause')) wrap.classList.add('is-paused');
            else { wrap.classList.remove('is-paused'); autoplayAll(); }
          });
        });
      }

      initMarquee(marquee, 0.6);
    });
  }

  function initReviewsCarousel(section) {
    qsa('.lp-social__marquee', section).forEach(function (marquee) {
      var origCards = Array.prototype.slice.call(marquee.children);
      if (!origCards.length) return;
      origCards.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        marquee.appendChild(clone);
      });
      initMarquee(marquee, 0.4);
    });
  }

  /* ─────────────────────────────────────────────
     STICKY BAR (per section)
  ───────────────────────────────────────────── */
  function initStickyBar(section) {
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return;

    var sticky = document.getElementById('gri-sticky-' + sectionId);
    if (!sticky) return;

    var heroBuyBtn = qs('.lp-fhero__buy-pill', section);
    var triggerPoint = heroBuyBtn
      ? heroBuyBtn.getBoundingClientRect().bottom + window.scrollY
      : 400;

    var chip       = qs('.lp-sticky__selected', sticky);
    var picker     = qs('.lp-sticky__picker', sticky);
    var options    = qsa('.lp-sticky__opt', sticky);
    var stickyCta  = qs('.lp-sticky__cta', sticky);
    var priceNowEl = qs('[data-sticky-price-now]', sticky);
    var priceWasEl = qs('[data-sticky-price-was]', sticky);
    var thumbEl    = qs('[data-sticky-selected-thumb]', sticky);
    var nameEl     = qs('[data-sticky-selected-name]', sticky);

    var isSyncing = false;

    function openPicker() {
      if (!picker) return;
      picker.hidden = false;
      // eslint-disable-next-line no-unused-expressions
      picker.offsetHeight;
      picker.classList.add('is-open');
      if (chip) chip.setAttribute('aria-expanded', 'true');
    }
    function closePicker() {
      if (!picker) return;
      picker.classList.remove('is-open');
      if (chip) chip.setAttribute('aria-expanded', 'false');
      setTimeout(function () {
        if (!picker.classList.contains('is-open')) picker.hidden = true;
      }, 220);
    }
    function togglePicker() {
      if (!picker) return;
      if (picker.classList.contains('is-open')) closePicker();
      else openPicker();
    }

    function onScroll() {
      if (window.scrollY > triggerPoint) sticky.classList.add('is-visible');
      else { sticky.classList.remove('is-visible'); closePicker(); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      if (heroBuyBtn) {
        triggerPoint = heroBuyBtn.getBoundingClientRect().bottom + window.scrollY;
      }
    });
    onScroll();

    if (chip && picker) {
      chip.addEventListener('click', function (e) {
        e.stopPropagation();
        togglePicker();
      });
      document.addEventListener('click', function (e) {
        if (!picker.classList.contains('is-open')) return;
        if (e.target.closest('#gri-sticky-' + sectionId)) return;
        closePicker();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && picker.classList.contains('is-open')) {
          closePicker();
          chip.focus();
        }
      });
    }

    function applyVariant(opt, syncToMain) {
      if (!opt) return;
      options.forEach(function (o) {
        o.classList.remove('is-selected');
        o.setAttribute('aria-checked', 'false');
      });
      opt.classList.add('is-selected');
      opt.setAttribute('aria-checked', 'true');

      var rawVid = opt.getAttribute('data-variant-id');
      var vid = sanitiseVariantId(rawVid);
      var title = opt.getAttribute('data-title') || '';
      var price = opt.getAttribute('data-price') || '';
      var cmp   = opt.getAttribute('data-compare-price') || '';

      if (stickyCta && vid) stickyCta.setAttribute('data-variant-id', vid);

      if (nameEl) nameEl.textContent = title;
      if (thumbEl) {
        var optImg = qs('img', opt);
        var chipImg = qs('img', thumbEl);
        if (optImg) {
          if (chipImg) {
            chipImg.src = optImg.src;
            chipImg.alt = optImg.alt || title;
          } else {
            var newImg = document.createElement('img');
            newImg.src = optImg.src;
            newImg.alt = optImg.alt || title;
            newImg.loading = 'lazy';
            thumbEl.innerHTML = '';
            thumbEl.appendChild(newImg);
          }
        }
      }

      if (priceNowEl && price) priceNowEl.textContent = price;
      if (priceWasEl) {
        if (cmp) { priceWasEl.textContent = cmp; priceWasEl.style.display = ''; }
        else { priceWasEl.style.display = 'none'; }
      }

      if (syncToMain && !isSyncing && vid) {
        isSyncing = true;
        qsa('.lp-bb__tile[data-variant-id]', section).forEach(function (t) {
          if (t.getAttribute('data-variant-id') === vid) t.click();
        });
        setTimeout(function () { isSyncing = false; }, 100);
      }
    }

    options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        applyVariant(opt, true);
        closePicker();
      });
    });

    qsa('.lp-bb__tile[data-variant-id]', section).forEach(function (tile) {
      tile.addEventListener('click', function () {
        if (isSyncing) return;
        var vid = tile.getAttribute('data-variant-id');
        if (!vid) return;
        var match = options.filter(function (o) {
          return o.getAttribute('data-variant-id') === vid;
        })[0];
        if (match) {
          isSyncing = true;
          applyVariant(match, false);
          setTimeout(function () { isSyncing = false; }, 100);
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     COLOUR TOKENS (per section)
  ───────────────────────────────────────────── */
  function injectColourTokens(section) {
    var primary = section.getAttribute('data-primary');
    var accent  = section.getAttribute('data-accent');
    if (primary) section.style.setProperty('--gri-primary', primary);
    if (accent)  section.style.setProperty('--gri-accent', accent);
  }

  /* ─────────────────────────────────────────────
     DASHED ARROWS (per section)
  ───────────────────────────────────────────── */
  function initArrows(section) {
    var svg = qs('.lp-fhero__arrows', section);
    var center = qs('.lp-fhero__stage', section);
    if (!svg || !center) return;

    var leftOrbits  = qsa('.lp-fhero__col--left .lp-fhero__orbit', section);
    var rightOrbits = qsa('.lp-fhero__col--right .lp-fhero__orbit', section);
    var allPills = [];
    leftOrbits.forEach(function (el) { allPills.push({ el: el, side: 'left' }); });
    rightOrbits.forEach(function (el) { allPills.push({ el: el, side: 'right' }); });
    if (!allPills.length) return;

    var cRect = center.getBoundingClientRect();
    var W = cRect.width, H = cRect.height;
    if (W < 10 || H < 10) return;

    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    svg.setAttribute('width', W);
    svg.setAttribute('height', H);

    var markerId = (svg.id || '').replace('gri-arrows-', '') || 'gri';
    var mid = 'arr-' + markerId;

    if (!svg.querySelector('marker')) {
      svg.insertAdjacentHTML('afterbegin',
        '<defs><marker id="' + mid + '" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">'
        + '<path d="M1 1.5 L7.5 4.5 L1 7.5" fill="none" stroke="#E43F7B"'
        + ' stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
        + '</marker></defs>');
    } else {
      var existing = svg.querySelector('marker');
      if (existing) mid = existing.id;
    }

    var paths = '';
    allPills.forEach(function (p) {
      var r = p.el.getBoundingClientRect();
      var midY = r.top - cRect.top + r.height / 2;
      var endX, startX;
      if (p.side === 'left') { endX = r.right - cRect.left + 6; startX = W * 0.28; }
      else { endX = r.left - cRect.left - 6; startX = W * 0.72; }
      var cpX = (startX + endX) / 2, cpY = midY - 22;
      paths += '<path d="M' + startX.toFixed(1) + ' ' + midY.toFixed(1)
        + ' Q' + cpX.toFixed(1) + ' ' + cpY.toFixed(1)
        + ' ' + endX.toFixed(1) + ' ' + midY.toFixed(1) + '"'
        + ' fill="none" stroke="#E43F7B" stroke-width="2"'
        + ' stroke-dasharray="6 4" stroke-linecap="round"'
        + ' marker-end="url(#' + mid + ')"/>';
    });
    svg.insertAdjacentHTML('beforeend', paths);
  }

  function safeInitArrows(section) {
    var attempts = 0;
    function attempt() {
      attempts++;
      var center = qs('.lp-fhero__stage', section);
      if (!center) return;
      var r = center.getBoundingClientRect();
      if (r.width < 10 && attempts < 5) { setTimeout(attempt, attempts * 150); return; }
      initArrows(section);
    }
    attempt();
  }

  /* ─────────────────────────────────────────────
     INIT (per section)
  ───────────────────────────────────────────── */
  function initSection(section) {
    if (section._griInited) return;
    section._griInited = true;

    injectColourTokens(section);
    initVariantTiles(section);
    initAddToCart(section);
    initUGCRail(section);
    initReviewsCarousel(section);
    initStickyBar(section);

    if (document.readyState === 'complete') setTimeout(function () { safeInitArrows(section); }, 100);
    else window.addEventListener('load', function () { setTimeout(function () { safeInitArrows(section); }, 100); });

    window.addEventListener('resize', function () {
      var svg = qs('.lp-fhero__arrows', section);
      if (svg) {
        while (svg.lastChild && svg.lastChild.tagName === 'path') svg.removeChild(svg.lastChild);
      }
      safeInitArrows(section);
    });
  }

  function init() {
    getLandingSections().forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target.querySelector('.landed-lp[data-section-id]');
    if (sec && !sec.closest('.gri-mb-wrapper')) initSection(sec);
  });

})();